import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight } from "lucide-react";

const BACKDROP = [
  "/ap_real_mitch.jpeg",
  "/ap_real_02.jpeg",
  "/ap_real_03.jpeg",
  "/ap_real_04.jpeg",
  "/ap_real_05.jpeg",
  "/ap_real_06.jpeg",
  "/ap_real_07.jpeg",
];

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.14 },
  }),
};

const fadeAnim = {
  hidden: { opacity: 0, y: 24 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay },
  }),
};

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [backdropIdx, setBackdropIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setBackdropIdx((i) => (i + 1) % BACKDROP.length),
      5000
    );
    return () => clearInterval(t);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        {BACKDROP.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="AP Pumping crew and machinery on site"
            data-testid={`hero-backdrop-${i}`}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${
              i === backdropIdx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-[#0A0C10]/55 to-[#0A0C10]/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0C10]/85 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-32 sm:px-8 sm:pb-24">
        <motion.p
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={0.1}
          className="mb-6 inline-flex items-center gap-2 border border-[#0052FF]/50 bg-[#0052FF]/15 px-3 py-1.5 font-tech text-[11px] uppercase tracking-[0.25em] text-[#7EB0FF] sm:text-xs"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0052FF] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#0052FF]" />
          </span>
          Concrete pumping — on call when you need us
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tight">
          <span className="block overflow-hidden">
            <motion.span
              variants={lineAnim}
              initial="hidden"
              animate="show"
              custom={0}
              className="block text-[clamp(2.8rem,7.5vw,7rem)]"
            >
              Professional
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              variants={lineAnim}
              initial="hidden"
              animate="show"
              custom={1}
              className="block text-[clamp(2.8rem,7.5vw,7rem)]"
            >
              Concrete{" "}
              <span className="bg-[#0052FF] px-3 text-black sm:px-4">Pumping</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              variants={lineAnim}
              initial="hidden"
              animate="show"
              custom={2}
              className="hero-outline block text-[clamp(2.8rem,7.5vw,7rem)]"
            >
              Solutions
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={0.75}
          className="mt-7 max-w-xl text-base text-slate-300 sm:text-lg"
        >
          Safe, efficient and reliable concrete pumping for residential,
          commercial and civil projects. The right equipment. The right people.
          The job done right.
        </motion.p>

        <motion.div
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={0.9}
          className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <a
            href="tel:0450454453"
            data-testid="hero-call-now-button"
            className="group inline-flex items-center justify-center gap-3 bg-[#0052FF] px-8 py-4 font-display text-xl font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#2E74FF] hover:shadow-[0_0_36px_rgba(0,82,255,0.6)]"
          >
            <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
            Call 0450 454 453
          </a>
          <button
            onClick={() => {
              navigate("/quote");
              window.scrollTo(0, 0);
            }}
            data-testid="hero-quote-button"
            className="group inline-flex items-center justify-center gap-3 border border-white/25 px-8 py-4 font-display text-xl font-bold uppercase tracking-wider text-white transition-all duration-300 hover:border-[#0052FF] hover:text-[#7EB0FF]"
          >
            Quick quote
            <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>
        </motion.div>

        <motion.div
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={1.05}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6 font-tech text-[11px] uppercase tracking-[0.2em] text-slate-400 sm:text-xs"
        >
          <li data-testid="hero-capability-line-pumping">Line &amp; boom pumping</li>
          <li data-testid="hero-capability-insured">Fully insured</li>
          <li data-testid="hero-capability-availability">When needed, we'll be there</li>
        </motion.div>
      </div>
    </section>
  );
}